import { useEventListener, useResizeObserver } from '@vueuse/core'
import { onBeforeUnmount, onMounted, watch } from 'vue'

interface UseFloatingHeaderOptions {
  enabled: () => boolean
  bodyContainer: () => HTMLElement | null | undefined
  overlay: () => HTMLElement | null | undefined
  offset: () => number
}

/**
 * Drives a cloned, page-scroll sticky table header: shows the overlay once the
 * real header scrolls above `offset` (and the table is still in view), keeps it
 * pixel-aligned to the body table, and mirrors the body's horizontal scroll.
 */
export function useFloatingHeader(options: UseFloatingHeaderOptions): void {
  const { enabled, bodyContainer, overlay, offset } = options

  let frame = 0

  function setVisibility(ov: HTMLElement, value: 'visible' | 'hidden') {
    ov.style.setProperty('visibility', value, 'important')
  }

  function hide() {
    const ov = overlay()

    if (ov) {
      setVisibility(ov, 'hidden')
    }
  }

  function update() {
    frame = 0
    const container = bodyContainer()
    const ov = overlay()

    if (!enabled() || !container || !ov) {
      hide()
      return
    }

    const rect = container.getBoundingClientRect()
    const off = offset()

    const visible = rect.top < off && rect.bottom > off + ov.offsetHeight
    setVisibility(ov, visible ? 'visible' : 'hidden')

    if (visible) {
      ov.style.left = `${rect.left}px`
      ov.style.width = `${rect.width}px`
      ov.scrollLeft = container.scrollLeft
      // Mirror the body's scrolled-x flag so the pinned-column divider (keyed off
      // `[data-slot='table-container'][data-scrolled-x]`) shows in the clone too.
      ov.toggleAttribute('data-scrolled-x', container.scrollLeft > 0)
      ov.toggleAttribute('data-scroll-x-end', container.scrollLeft + container.clientWidth >= container.scrollWidth - 1)
    }
  }

  function schedule() {
    if (frame) {
      return
    }

    frame = requestAnimationFrame(update)
  }

  onMounted(() => {
    useEventListener(() => (enabled() ? window : null), ['scroll', 'resize'], schedule, { passive: true })
    useEventListener(() => (enabled() ? bodyContainer() : null), 'scroll', schedule, { passive: true })
    useResizeObserver(() => (enabled() ? bodyContainer() : null), schedule)

    watch(enabled, (isEnabled) => (isEnabled ? schedule() : hide()), { immediate: true })
  })

  onBeforeUnmount(() => {
    if (frame) {
      cancelAnimationFrame(frame)
    }
  })
}
